import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

export const Footer = () => {
  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/letting-agents', label: 'For Letting Agents' },
    { path: '/process', label: 'How It Works' },
    { path: '/testimonials', label: 'Reviews' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact' }
  ];

  const areas = [
    { path: '/locksmith-romford', label: 'Romford' },
    { path: '/locksmith-hornchurch', label: 'Hornchurch' },
    { path: '/locksmith-upminster', label: 'Upminster' },
    { path: '/locksmith-dagenham', label: 'Dagenham' },
    { path: '/locksmith-barking', label: 'Barking' },
    { path: '/locksmith-ilford', label: 'Ilford' },
    { path: '/locksmith-stratford', label: 'Stratford' },
    { path: '/locksmith-canary-wharf', label: 'Canary Wharf' },
    { path: '/locksmith-chelmsford', label: 'Chelmsford' }
  ];

  const linkStyle = {
    color: 'var(--text-on-dark-muted)',
    textDecoration: 'none',
    fontSize: '0.925rem',
    transition: 'color 0.25s ease',
    display: 'inline-block'
  };
  const onHoverEnter = (e) => (e.currentTarget.style.color = 'var(--royal-gold-bright)');
  const onHoverLeave = (e) => (e.currentTarget.style.color = 'var(--text-on-dark-muted)');

  return (
    <footer style={{ background: 'var(--royal-dark-deepest)', color: 'var(--text-on-dark-primary)' }} data-testid="site-footer">
      {/* Subtle gold hairline accent at the top */}
      <div style={{
        height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, var(--royal-gold-border) 20%, var(--royal-gold) 50%, var(--royal-gold-border) 80%, transparent 100%)'
      }} aria-hidden="true" />

      <div className="royal-container royal-crown-texture" style={{ paddingTop: '72px', paddingBottom: '32px' }}>
        {/* Logo + columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
          gap: '48px',
          marginBottom: '48px'
        }}>
          {/* Brand column */}
          <div style={{ gridColumn: 'span 1', minWidth: 0 }}>
            <Link
              to="/admin"
              style={{ textDecoration: 'none', display: 'inline-block', marginBottom: '20px' }}
              title="Admin"
              data-testid="footer-admin-link"
            >
              <img
                src="/brand/royal-logo-main.png"
                alt="Royal Security — Doors & Windows"
                style={{ maxWidth: '260px', width: '100%', height: 'auto', display: 'block' }}
              />
            </Link>
            <p style={{
              fontSize: '0.925rem',
              lineHeight: '1.7',
              color: 'var(--text-on-dark-muted)',
              marginBottom: '16px'
            }}>
              Based in Romford, specialist door, window, and lock engineers serving homeowners, landlords, and property managers across London and Essex.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '18px' }}>
              {['Fully Insured', 'Guaranteed Work', '24/7 Emergency'].map((b) => (
                <span key={b} style={{
                  padding: '5px 12px',
                  background: 'var(--royal-gold-surface)',
                  border: '1px solid var(--royal-gold-border)',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'var(--royal-gold-bright)'
                }}>{b}</span>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '0.95rem',
              fontWeight: '700',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '20px',
              color: 'var(--royal-gold-bright)'
            }}>
              Quick Links
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '11px' }}>
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} style={linkStyle} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave} data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '0.95rem',
              fontWeight: '700',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '20px',
              color: 'var(--royal-gold-bright)'
            }}>
              Service Areas
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {areas.map((area) => (
                <li key={area.path}>
                  <Link to={area.path} style={{ ...linkStyle, fontSize: '0.875rem' }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
                    {area.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '0.95rem',
              fontWeight: '700',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '20px',
              color: 'var(--royal-gold-bright)'
            }}>
              Get In Touch
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <a href="tel:07710613783" style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: '12px' }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave} data-testid="footer-phone">
                <Phone size={16} style={{ color: 'var(--royal-gold)' }} />
                <span>07710 613783</span>
              </a>
              <a href="mailto:Joe@RoyalSecurity.info" style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: '12px' }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave} data-testid="footer-email">
                <Mail size={16} style={{ color: 'var(--royal-gold)' }} />
                <span>Joe@RoyalSecurity.info</span>
              </a>
              <div style={{ ...linkStyle, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <MapPin size={16} style={{ marginTop: '3px', flexShrink: 0, color: 'var(--royal-gold)' }} />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '2px', color: 'var(--text-on-dark-primary)' }}>Romford, Essex</div>
                  <div style={{ fontSize: '0.825rem', lineHeight: '1.5', color: 'var(--text-on-dark-dimmed)' }}>Serving London & Essex</div>
                </div>
              </div>
              <div style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Clock size={16} style={{ color: 'var(--royal-gold)' }} />
                <span>Mon–Sat · Emergency 24/7</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gold hairline divider */}
        <hr className="royal-divider-gold" style={{ margin: '24px 0' }} />

        {/* Bottom bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          fontSize: '0.825rem',
          color: 'var(--text-on-dark-dimmed)'
        }}>
          <div>
            © {new Date().getFullYear()} Royal Security · <span style={{ color: 'var(--royal-gold)' }}>Doors & Windows</span>. All rights reserved.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Link to="/" style={{ color: 'var(--text-on-dark-dimmed)', textDecoration: 'none' }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>Home</Link>
            <Link to="/contact" style={{ color: 'var(--text-on-dark-dimmed)', textDecoration: 'none' }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>Contact</Link>
            <span style={{ color: 'var(--royal-gold)', letterSpacing: '0.1em', fontSize: '0.75rem', fontWeight: 600 }}>UK · ESTABLISHED SPECIALISTS</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
